import type { Ref } from 'vue'
import {
    getWorkbenchRouteTargetBySecondary,
    type SecondaryTabId,
} from '~/utils/creationIntentRegistry'
import { emitter } from '~/utils/emitter'

export type WorkbenchHandoffEnvelope<TPrefill> = {
    intent?: string
    ts?: number
    prefill?: TPrefill
}

export type ConsumeWorkbenchHandoffResult<TPrefill> =
    | { found: false }
    | {
          found: true
          duplicate: boolean
          intent: string
          ts: number | null
          prefill: TPrefill
      }

type ConsumeWorkbenchHandoffOptions<TPrefill> = {
    secondaryId: SecondaryTabId
    /** In-memory guard for keepalive / repeated handler calls. */
    lastHandledTs: Ref<number | null>
    /** sessionStorage key storing last handled ts (cross-remount guard in same tab). */
    lastTsStorageKey: string
    /** Optional log prefix (e.g. "[text-to-image]") */
    logTag?: string
    /** Fallback handoff key (should rarely be needed; kept for safety). */
    fallbackHandoffKey?: string
    /** Optional fallback for env when JSON parsing fails. */
    parseFallback?: WorkbenchHandoffEnvelope<TPrefill>
}

const WORKBENCH_SUBMIT_LOADING_KEY = 'wery_workbench_submit_loading'

/**
 * Workbench handoff (PcWorkbench -> tool pages)
 * - Reads envelope from sessionStorage using `handoffKey` resolved by secondary id
 * - Removes handoffKey immediately (regardless parse success)
 * - Applies dual de-dup guards:
 *   - in-memory `lastHandledTs`
 *   - sessionStorage `lastTsStorageKey`
 */
export function useWorkbenchJumpHandoff() {
    const readWorkbenchSubmitLoading = () => {
        if (!import.meta.client || typeof window === 'undefined') {
            return false
        }

        try {
            return window.sessionStorage.getItem(WORKBENCH_SUBMIT_LOADING_KEY) === '1'
        } catch (e) {
            console.warn('[workbench-handoff] read submit loading failed:', e)
            return false
        }
    }

    const setWorkbenchSubmitLoading = (flag: boolean) => {
        if (!import.meta.client || typeof window === 'undefined') return

        try {
            if (flag) {
                window.sessionStorage.setItem(WORKBENCH_SUBMIT_LOADING_KEY, '1')
            } else {
                window.sessionStorage.removeItem(WORKBENCH_SUBMIT_LOADING_KEY)
            }
        } catch (e) {
            console.warn('[workbench-handoff] write submit loading failed:', e)
        }

        emitter.emit('workbench-submit-loading-change', flag)
    }

    const runWorkbenchHandoffAutoCreate = async <T>(task: () => Promise<T> | T) => {
        try {
            return await task()
        } finally {
            setWorkbenchSubmitLoading(false)
        }
    }

    const consumeWorkbenchHandoff = <TPrefill = any>(
        opts: ConsumeWorkbenchHandoffOptions<TPrefill>
    ): ConsumeWorkbenchHandoffResult<TPrefill> => {
        if (!import.meta.client || typeof window === 'undefined') {
            return { found: false }
        }

        const target = getWorkbenchRouteTargetBySecondary(opts.secondaryId)
        const handoffKey = target?.handoffKey || opts.fallbackHandoffKey || ''
        if (!handoffKey) return { found: false }

        const raw = window.sessionStorage.getItem(handoffKey)
        if (!raw) return { found: false }

        // parse first; regardless of success, clear to avoid repeated attempts
        let env: WorkbenchHandoffEnvelope<TPrefill> | null =
            opts.parseFallback || null
        try {
            env = JSON.parse(raw) as WorkbenchHandoffEnvelope<TPrefill>
        } catch (e) {
            const tag = opts.logTag ? `${opts.logTag} ` : ''
            console.warn(`${tag}parse workbench handoff failed:`, e)
        } finally {
            window.sessionStorage.removeItem(handoffKey)
        }

        const ts = typeof env?.ts === 'number' ? env.ts : null
        const intent = typeof env?.intent === 'string' ? env.intent : ''
        const prefill = ((env?.prefill || {}) as unknown) as TPrefill

        if (ts) {
            // in-memory guard (same keepalive instance)
            if (opts.lastHandledTs.value === ts) {
                return { found: true, duplicate: true, intent, ts, prefill }
            }

            // sessionStorage guard (refresh / remount in same tab)
            const prevTsRaw = window.sessionStorage.getItem(opts.lastTsStorageKey)
            const prevTs = prevTsRaw ? Number(prevTsRaw) : NaN
            if (Number.isFinite(prevTs) && prevTs === ts) {
                return { found: true, duplicate: true, intent, ts, prefill }
            }

            opts.lastHandledTs.value = ts
            window.sessionStorage.setItem(opts.lastTsStorageKey, String(ts))
        }

        return { found: true, duplicate: false, intent, ts, prefill }
    }

    return {
        consumeWorkbenchHandoff,
        readWorkbenchSubmitLoading,
        setWorkbenchSubmitLoading,
        runWorkbenchHandoffAutoCreate,
    }
}

